import { useEffect, useRef, useState } from 'react';

type EqualityFn<T> = (a: T, b: T) => boolean;

export function useDebounce<T>(
  value: T,
  delay: number = 300,
  options?: { equalityFn?: EqualityFn<T> }
): T {
  const { equalityFn = Object.is } = options ?? {};
  const [debounced, setDebounced] = useState<T>(value);

  const valueRef = useRef(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (equalityFn(valueRef.current, value)) return;

    valueRef.current = value;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setDebounced(valueRef.current);
      timerRef.current = null;
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [value, delay, equalityFn]);

  return debounced;
}
